import { useEffect, useState } from 'react'
import { Button } from '@web/components/ui/Button'
import { ApiClientError } from '@web/lib/api'
import { useExercise, useUpdateExercise } from '@web/lib/admin'

/**
 * Edits one exercise's prompt and content.
 *
 * The payload is edited as JSON on purpose. A bespoke form per exercise type
 * would be eight forms to build and keep in sync with the schema, for a
 * teacher who edits a handful of items; raw JSON plus **server-side schema
 * validation** gets the same safety with none of the drift. A malformed edit
 * is rejected by the same Zod union and integrity checks the seed builder
 * runs, so nothing that would crash a lesson can be saved.
 */
export function ExerciseEditor({
  exerciseId,
  onClose,
}: {
  exerciseId: string
  onClose: () => void
}) {
  const { data, isPending } = useExercise(exerciseId)
  const update = useUpdateExercise()

  const [prompt, setPrompt] = useState('')
  const [difficulty, setDifficulty] = useState(1)
  const [payloadText, setPayloadText] = useState('')
  const [jsonError, setJsonError] = useState<string | null>(null)

  useEffect(() => {
    if (!data) return
    setPrompt(data.exercise.prompt)
    setDifficulty(data.exercise.difficulty)
    setPayloadText(JSON.stringify(data.exercise.payload, null, 2))
  }, [data])

  function save() {
    let payload: unknown
    try {
      payload = JSON.parse(payloadText)
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON')
      return
    }
    setJsonError(null)
    update.mutate(
      { id: exerciseId, prompt, difficulty, payload },
      { onSuccess: onClose },
    )
  }

  const serverError =
    update.error instanceof ApiClientError ? update.error : null
  const details = Array.isArray(serverError?.details) ? serverError.details : null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Edit exercise"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/40 p-4 backdrop-blur-sm"
    >
      <div className="my-8 w-full max-w-2xl rounded-card bg-white p-6 shadow-xl">
        {isPending || !data ? (
          <p className="text-ink-500">Loading…</p>
        ) : (
          <>
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-xl font-bold text-ink-900">Edit exercise</h2>
                <p className="mt-0.5 text-sm text-ink-500">
                  {data.exercise.lessonTitle} · {data.exercise.type}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="text-2xl leading-none text-ink-400 hover:text-ink-900"
              >
                ×
              </button>
            </div>

            <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="ex-prompt">
              Prompt
            </label>
            <input
              id="ex-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="mb-4 min-h-11 w-full rounded-xl bg-white px-3 text-base ring-1 ring-sand-200 focus:ring-2 focus:ring-brand-600"
            />

            <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="ex-diff">
              Difficulty (1–5)
            </label>
            <input
              id="ex-diff"
              type="number"
              min={1}
              max={5}
              value={difficulty}
              onChange={(e) => setDifficulty(Number(e.target.value))}
              className="mb-4 min-h-11 w-24 rounded-xl bg-white px-3 text-base ring-1 ring-sand-200 focus:ring-2 focus:ring-brand-600"
            />

            <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="ex-payload">
              Content (JSON)
            </label>
            <textarea
              id="ex-payload"
              rows={14}
              value={payloadText}
              onChange={(e) => setPayloadText(e.target.value)}
              spellCheck={false}
              className="w-full rounded-xl bg-sand-50 px-3 py-2 font-mono text-xs ring-1 ring-sand-200 focus:ring-2 focus:ring-brand-600"
            />

            {jsonError && (
              <p role="alert" className="mt-2 text-sm text-clay-700">
                Invalid JSON: {jsonError}
              </p>
            )}

            {serverError && (
              <div role="alert" className="mt-2 rounded-xl bg-clay-50 px-3 py-2 text-sm text-clay-700">
                <p>{serverError.message}</p>
                {details && (
                  <ul className="mt-1 list-inside list-disc">
                    {details.map((d, i) => (
                      <li key={i}>
                        {typeof d === 'string'
                          ? d
                          : `${(d as { path?: string }).path ?? ''} ${(d as { message?: string }).message ?? ''}`}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={save} loading={update.isPending}>
                Save
              </Button>
            </div>

            <p className="mt-3 text-xs text-ink-400">
              Saved edits are preserved by the seed script and reported so they can be
              folded back into the content files.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
